import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logging/logger';
import { sharedFetch, ATTENDANCE_TODAY_KEY } from '../../services/sharedDashboardFetch';
import { isCheckInWindowActive } from '../dashboard/activeAttendanceSessionsHelpers';

const POLL_INTERVAL_MS = 60000; // 1 minute - fine-grained enough for a 30-minute cadence
const PROMPT_INTERVAL_MINUTES = 30;

// Global "you have a live check-in you haven't done yet" reminder. Runs independently
// of the dashboard's own attendance polling so it fires no matter what page the
// student is on. /attendance/active-today already filters to the student's own
// active enrollments server-side, and reports is_checked_in per session, so eligibility
// here only needs to check: window currently open, not yet checked in, and at least
// one PROMPT_INTERVAL_MINUTES multiple has elapsed since the window opened.
export default function useGlobalCheckInPrompt() {
  const { user } = useAuth();
  const userId = user?.id;
  const userRole = user?.role;
  const { pathname } = useLocation();

  // Quizzes, midterms, and finals all route through the same /student/attempt/:attemptId
  // player regardless of quiz type - suppress the reminder anywhere on that page so it
  // never interrupts an in-progress attempt.
  const isQuizAttemptPage = useMemo(() => pathname.includes('/student/attempt/'), [pathname]);

  const [queue, setQueue] = useState([]);
  const [checkingIn, setCheckingIn] = useState(false);
  const [error, setError] = useState('');
  // session_id -> last 30-minute slot number a prompt was shown for. Prevents
  // re-queueing the same session on every poll within the same 30-minute window.
  const shownSlotsRef = useRef({});

  const evaluate = useCallback((sessions) => {
    const now = Date.now();
    const eligible = [];
    const seenIds = new Set();

    sessions.forEach((session) => {
      const id = session.session_id;
      if (id === undefined || id === null) return;
      seenIds.add(String(id));

      if (session.is_checked_in) {
        delete shownSlotsRef.current[id];
        return;
      }
      if (!isCheckInWindowActive(session) || !session.start_time) return;

      const startMs = new Date(session.start_time).getTime();
      if (Number.isNaN(startMs)) return;

      const elapsedMinutes = (now - startMs) / 60000;
      const slot = Math.floor(elapsedMinutes / PROMPT_INTERVAL_MINUTES);
      if (slot < 1) return; // hasn't hit the first 30-minute mark yet

      const lastShown = shownSlotsRef.current[id];
      if (lastShown === undefined || slot > lastShown) {
        eligible.push(session);
      }
    });

    // Drop tracking for sessions no longer returned (ended, deleted, etc.)
    Object.keys(shownSlotsRef.current).forEach((id) => {
      if (!seenIds.has(String(id))) delete shownSlotsRef.current[id];
    });

    if (eligible.length === 0) return;

    setQueue((prev) => {
      const existingIds = new Set(prev.map((s) => s.session_id));
      const additions = eligible.filter((s) => !existingIds.has(s.session_id));
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, []);

  // `allowShared` is opt-in and only the mount call passes it -- see the same
  // note in useActiveAttendanceSessions, the other component that requests this
  // endpoint on a full page load. Polls and focus refreshes stay unshared.
  const fetchAndEvaluate = useCallback(async ({ allowShared = false } = {}) => {
    if (!userId || userRole !== 'student' || isQuizAttemptPage) return;
    try {
      const endpoint = '/attendance/active-today?v=' + Date.now();
      const response = allowShared
        ? await sharedFetch(ATTENDANCE_TODAY_KEY, () => apiService.request(endpoint))
        : await apiService.request(endpoint);
      if (response.success) {
        evaluate(response.sessions || []);
      }
    } catch (e) {
      // Non-critical background poll - log and let the next interval retry.
      logger.debug('GlobalCheckInPrompt: failed to fetch active sessions', { error: e });
    }
  }, [userId, userRole, isQuizAttemptPage, evaluate]);

  useEffect(() => {
    if (!userId || userRole !== 'student') {
      setQueue([]);
      return undefined;
    }

    fetchAndEvaluate({ allowShared: true });
    const interval = setInterval(() => fetchAndEvaluate(), POLL_INTERVAL_MS);
    const handleFocus = () => fetchAndEvaluate();
    window.addEventListener('focus', handleFocus);

    // Drop a session from the queue immediately if it gets checked in elsewhere
    // (e.g. the dashboard's own check-in button), rather than leaving a stale
    // "you haven't checked in" prompt showing for up to another minute.
    const handleCheckedInElsewhere = (e) => {
      const sessionId = e.detail?.sessionId;
      if (sessionId === undefined || sessionId === null) return;
      delete shownSlotsRef.current[sessionId];
      setQueue((prev) => prev.filter((s) => s.session_id !== sessionId));
    };
    window.addEventListener('attendance:checked-in', handleCheckedInElsewhere);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('attendance:checked-in', handleCheckedInElsewhere);
    };
  }, [userId, userRole, fetchAndEvaluate]);

  const currentSession = queue[0] || null;

  // Record the slot as "shown" only once the session actually reaches the front of
  // the queue AND is actually visible (not suppressed for a quiz attempt) - not when
  // it first becomes eligible - so a session waiting behind another prompt, or one
  // suppressed during an exam, doesn't get marked before the student ever sees it.
  useEffect(() => {
    if (isQuizAttemptPage || !currentSession?.start_time || !currentSession?.session_id) return;
    const startMs = new Date(currentSession.start_time).getTime();
    if (Number.isNaN(startMs)) return;
    const elapsedMinutes = (Date.now() - startMs) / 60000;
    shownSlotsRef.current[currentSession.session_id] = Math.floor(elapsedMinutes / PROMPT_INTERVAL_MINUTES);
  }, [currentSession, isQuizAttemptPage]);

  const dismiss = useCallback(() => {
    setQueue((prev) => prev.slice(1));
    setError('');
  }, []);

  const checkIn = useCallback(async () => {
    if (!currentSession) return;
    try {
      setCheckingIn(true);
      setError('');
      const response = await apiService.checkInAttendance(currentSession.session_id, 'present');
      if (response.success) {
        setQueue((prev) => prev.slice(1));
        // Let the dashboard's "My Active Sessions" card (a separate, independently
        // polling component) know right away instead of waiting up to 2 minutes
        // for its next poll - it may be open behind this modal right now.
        window.dispatchEvent(new CustomEvent('attendance:checked-in', {
          detail: { sessionId: currentSession.session_id }
        }));
      } else {
        setError(response.message || response.error || 'Failed to check in for attendance');
      }
    } catch (e) {
      setError(e?.message || 'Failed to check in for attendance. Please try again.');
    } finally {
      setCheckingIn(false);
    }
  }, [currentSession]);

  return {
    session: currentSession,
    open: !!currentSession && !isQuizAttemptPage,
    checkingIn,
    error,
    dismiss,
    checkIn
  };
}
